import { useState, useCallback, useEffect } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QuickBooksPurchaseOrder {
  purchasePrice: number;
  purchaseOrderNumber: string;
  purchaseOrderDate: string;
  invoiceNumber: string;
  customFieldNERV: string;
}

const QUICKBOOKS_PENDING_AUTH_PREFIX = 'figabl_quickbooks_pending_auth:';

/**
 * localStorage key for one connection attempt, so parallel attempts don't
 * overwrite each other. Keyed by a hash so the OAuth state itself is never stored.
 */
export async function quickBooksPendingAuthKey(state: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(state));
  const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${QUICKBOOKS_PENDING_AUTH_PREFIX}${hex}`;
}

export function useQuickBooks(organisationId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<QuickBooksPurchaseOrder[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  const QUICKBOOKS_COMPANY_ID = '9341455361959454';

  // Check connection status using backend status endpoint
  const checkConnectionStatus = useCallback(async () => {
    try {
      const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
        body: {
          action: 'status',
          companyId: QUICKBOOKS_COMPANY_ID,
          organisationId,
        }
      });

      if (error) {
        setIsConnected(false);
        return false;
      }

      const isConnected = data.connected === true;
      setIsConnected(isConnected);
      
      // If connection failed and requires reauth, show appropriate message
      if (!isConnected && data.requiresReauth) {
        toast.error('QuickBooks connection expired. Please reconnect.');
      }
      
      return isConnected;
    } catch (error) {
      console.error('Error checking QuickBooks connection status:', error);
      setIsConnected(false);
      return false;
    }
  }, [QUICKBOOKS_COMPANY_ID, organisationId]);

  const connectToQuickBooks = useCallback(async () => {
    try {
      const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
        body: { action: 'auth', organisationId }
      });

      if (error) throw error;

      // The callback page runs in the popup and needs the organisation to send back.
      localStorage.setItem(await quickBooksPendingAuthKey(data.state), organisationId);
      
      // Only set connecting state after we successfully get the auth URL
      setIsConnecting(true);
      
      // Open QuickBooks OAuth in a popup
      const popup = window.open(
        data.authUrl,
        'quickbooks-auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );
      
      // Handle popup blocked or failed to open
      if (!popup) {
        setIsConnecting(false);
        toast.error('Popup blocked. Please allow popups and try again.');
        return;
      }
      
      // Poll for popup closure
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          // Check connection status and fetch purchase orders after connection
          setTimeout(async () => {
            const connected = await checkConnectionStatus();
            if (connected) {
              fetchPurchaseOrders();
            }
          }, 1000);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error connecting to QuickBooks:', error);
      toast.error('Failed to connect to QuickBooks');
      setIsConnecting(false);
    }
  }, [organisationId]);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await invokeFunction(supabase, 'quickbooks-oauth', {
        body: {
          action: 'fetch-purchase-orders',
          companyId: QUICKBOOKS_COMPANY_ID,
          organisationId,
        }
      });

      if (error) throw error;

      setPurchaseOrders(data.purchaseOrders || []);
      toast.success(`Loaded ${data.purchaseOrders?.length || 0} purchase orders`);
      
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      const errorMessage = error.message || 'Failed to fetch purchase orders from QuickBooks';
      
      if (errorMessage.includes('not connected')) {
        toast.error('Please connect to QuickBooks first');
      } else if (errorMessage.includes('Forbidden')) {
        toast.error('QuickBooks access denied. Please reconnect with proper permissions.');
      } else {
        toast.error(errorMessage);
      }
      setPurchaseOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [organisationId]);

  // Check connection status on component mount
  useEffect(() => {
    const initializeConnection = async () => {
      const connected = await checkConnectionStatus();
      if (connected) {
        fetchPurchaseOrders();
      }
      setIsCheckingConnection(false);
    };
    initializeConnection();
  }, [checkConnectionStatus, fetchPurchaseOrders]);

  return {
    isLoading,
    purchaseOrders,
    fetchPurchaseOrders,
    connectToQuickBooks,
    isConnected,
    isConnecting,
    isCheckingConnection,
  };
}